import { isEmpty, isEqual, xorWith } from 'lodash';

import { GenericObject } from '@app/components/common/Table/DefaultTable/types';

import { FIELD_TYPES } from './validateUtils';

export const CONFIGURATION_TYPE = {
  MANUAL: 'CUSTOM',
  AUTO: 'AUTO',
};

export const ORIENTATION_TYPE = {
  HORIZONTAL: 'HORIZONTAL',
  VERTICAL: 'VERTICAL',
  SPOTLIGHT: 'SPOTLIGHT',
};

export const PAGE_TYPE = {
  TODAY: 'TODAY',
  DISCOVER: 'DISCOVER',
  RETIRE: 'RETIRE',
};

export const PODCAST_TYPE = {
  LATEST: 'LATEST',
  FEATURED: 'FEATURED',
};

export const AUTOMATED_RAIL_TYPE = {
  LATEST: 'LATEST',
  TOPIC_DRIVEN: 'TOPIC_DRIVEN',
  SERIES_DRIVEN: 'SERIES_DRIVEN',
};

export const SECTION_TYPE = {
  RAIL: 'RAIL',
  CTA: 'CTA',
  PODCAST: 'PODCAST',
  PODREF: 'PODREF',
  APP_DOWNLOAD_INFO: 'APP_DOWNLOAD_INFO',
  NEWSLETTER: 'NEWSLETTER',
};

export const UNEDITABLE_SECTION_TYPES = [SECTION_TYPE.APP_DOWNLOAD_INFO, SECTION_TYPE.PODREF, SECTION_TYPE.NEWSLETTER];

export const formFields = [
  { key: 'title', type: FIELD_TYPES.MULTILINGUAL, message: 'Name is required' },
  { key: 'section_type', type: FIELD_TYPES.SELECT, message: 'Section type is required' },
  { key: 'expiry_date', type: FIELD_TYPES.FUTURE_DATE, message: 'Expiry date must be after today' },
  { key: 'orientation_type', type: FIELD_TYPES.SELECT, message: 'Mobile App Orientation is required' },
];

export const isArrayEqual = (x: any, y: any) => isEmpty(xorWith(x, y, isEqual));

export const conditionalFields = [
  {
    key: 'cta_id',
    type: FIELD_TYPES.SELECT,
    message: 'CTA is required',
    shouldValidate: (values: GenericObject) => values.section_type === SECTION_TYPE.CTA,
  },
  {
    key: 'configuration_type',
    type: FIELD_TYPES.SELECT,
    message: 'Rail type is required',
    shouldValidate: (values: GenericObject) => values.section_type === SECTION_TYPE.RAIL,
  },
  {
    key: 'automated_rail_type',
    type: FIELD_TYPES.TEXT,
    message: 'Criteria for auto rail is required',
    shouldValidate: (values: GenericObject) =>
      values.section_type === SECTION_TYPE.RAIL && values.configuration_type === CONFIGURATION_TYPE.AUTO,
  },
  {
    key: 'taxonomy_id',
    type: FIELD_TYPES.SELECT,
    message: 'Topic is required',
    shouldValidate: (values: GenericObject) =>
      values.section_type === SECTION_TYPE.RAIL &&
      values.configuration_type === CONFIGURATION_TYPE.AUTO &&
      values.automated_rail_type === AUTOMATED_RAIL_TYPE.TOPIC_DRIVEN,
  },
  {
    key: 'event_series_id',
    type: FIELD_TYPES.SELECT,
    message: 'Series is required',
    shouldValidate: (values: GenericObject) =>
      values.section_type === SECTION_TYPE.RAIL &&
      values.configuration_type === CONFIGURATION_TYPE.AUTO &&
      values.automated_rail_type === AUTOMATED_RAIL_TYPE.SERIES_DRIVEN,
  },
  {
    key: 'number_of_vods',
    type: FIELD_TYPES.CUSTOM_VALIDATION,
    message: 'Maximum number of vods is required',
    shouldValidate: (values: GenericObject) => values.section_type === SECTION_TYPE.RAIL,
    customValidation: (value: number) => {
      return {
        validationResult: value > 0,
        validationMessage: 'Max number of VODs needs to be a number greater than 0',
      };
    },
  },
];

export const transformRails = (selected: GenericObject[], values: GenericObject) => {
  const defaultValues = {
    section_type: values.section_type,
    expiry_date: values.expiry_date,
    title: values.title,
    is_published: values.is_published,
    orientation_type: values.orientation_type,
    number_of_vods: values.number_of_vods ? parseInt(values.number_of_vods as string) : null,
    is_second_shuffling: false,
    rail_items: [],
    vod_ids: null,
    taxonomy_id: null,
    event_series_id: null,
    cta_id: null,
    page_type: values.page_type || PAGE_TYPE.TODAY,
  };

  if (values.section_type === SECTION_TYPE.RAIL && values.configuration_type === CONFIGURATION_TYPE.MANUAL) {
    return {
      ...defaultValues,
      configuration_type: values.configuration_type,
      vod_ids: selected.map((item) => item.id),
    };
  }

  if (values.section_type === SECTION_TYPE.RAIL && values.configuration_type === CONFIGURATION_TYPE.AUTO) {
    if (values.automated_rail_type === AUTOMATED_RAIL_TYPE.LATEST) {
      return {
        ...defaultValues,
        configuration_type: values.configuration_type,
        automated_rail_type: values.automated_rail_type,
        auto_rule: 'LATEST_CATCHUP_OF_SERIES',
      };
    }
    if (values.automated_rail_type === AUTOMATED_RAIL_TYPE.TOPIC_DRIVEN) {
      return {
        ...defaultValues,
        configuration_type: values.configuration_type,
        taxonomy_id: values.taxonomy_id,
        automated_rail_type: values.automated_rail_type,
      };
    }
    if (values.automated_rail_type === AUTOMATED_RAIL_TYPE.SERIES_DRIVEN) {
      return {
        ...defaultValues,
        configuration_type: values.configuration_type,
        event_series_id: values.event_series_id,
        automated_rail_type: values.automated_rail_type,
      };
    }
  }

  if (values.section_type === SECTION_TYPE.CTA) {
    return {
      ...defaultValues,
      cta_id: values.cta_id,
    };
  }

  if (values.section_type === SECTION_TYPE.PODCAST) {
    return {
      ...defaultValues,
      podcast_type: values.podcast_type,
      podcast_id: values.podcast_type === PODCAST_TYPE.FEATURED ? values.podcast_id : null,
    };
  }
};
